/**
 * Luồng thêm người dùng bằng EMAIL — FR-080…085, docs/02 §2.6.3.
 * Dùng chung cho `permissions.html` → *Thêm người dùng* và
 * `domain-detail.html` → *Chia sẻ quyền*.
 *
 * Bám nguyên cấu trúc DOM của prototype:
 *   .field > .email-lookup > input + button
 *   .stack[data-au-found]  > .lookup-card + .field + .setting-row
 *   .stack[data-au-invite] > .scope-note.warn + 3 .field + .setting-row
 */

import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { avatarTone } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { EMAIL_RE, permApi, type AssignableDomainRoleView, type UserLookupView } from './api';

export type EmailGrantSubmit =
  | { kind: 'grant'; userId: string; domainRoleId: string; notify: boolean }
  | {
      kind: 'invite';
      email: string;
      domainRoleId: string;
      displayName: string | null;
      message: string | null;
      expiresInDays: number;
    };

export interface EmailGrantFlowState {
  canSubmit: boolean;
  submitLabel: string;
  submit: () => EmailGrantSubmit | null;
  reset: () => void;
}

const EXPIRY_CHOICES = [7, 3, 30];

/**
 * Trả về phần thân hộp thoại + trạng thái nút gửi để hộp thoại cha dựng footer.
 * Cha truyền `onStateChange` để biết khi nào bật nút và nhãn nút là gì.
 */
export function EmailGrantFlow({
  domainId,
  roles,
  idPrefix,
  onStateChange,
}: {
  domainId: string;
  roles: AssignableDomainRoleView[];
  idPrefix: string;
  onStateChange: (state: EmailGrantFlowState) => void;
}) {
  const t = useT();
  const assignable = roles.filter((role) => role.assignable);
  const defaultRoleId = assignable.find((role) => role.isDefault)?.id ?? assignable[0]?.id ?? '';

  const [email, setEmail] = useState('');
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState<UserLookupView | null>(null);
  const [hint, setHint] = useState<string>(t('perm.lookupHint'));
  const [grantRoleId, setGrantRoleId] = useState(defaultRoleId);
  const [inviteRoleId, setInviteRoleId] = useState(defaultRoleId);
  const [notify, setNotify] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage] = useState(t('perm.inviteMessageDefault'));
  const [expiresInDays, setExpiresInDays] = useState(EXPIRY_CHOICES[0]!);

  useEffect(() => {
    if (!grantRoleId && defaultRoleId) setGrantRoleId(defaultRoleId);
    if (!inviteRoleId && defaultRoleId) setInviteRoleId(defaultRoleId);
  }, [defaultRoleId, grantRoleId, inviteRoleId]);

  const found = result?.found === true && result.alreadyGranted === false;
  const missing = result?.found === false;

  const reset = () => {
    setResult(null);
    setHint(t('perm.lookupHint'));
  };

  const submit = (): EmailGrantSubmit | null => {
    if (found && result?.user) {
      return { kind: 'grant', userId: result.user.id, domainRoleId: grantRoleId, notify };
    }
    if (missing) {
      return {
        kind: 'invite',
        email: email.trim().toLowerCase(),
        domainRoleId: inviteRoleId,
        displayName: displayName.trim() || null,
        message: message.trim() || null,
        expiresInDays,
      };
    }
    return null;
  };

  const resetAll = () => {
    setEmail('');
    setDisplayName('');
    setMessage(t('perm.inviteMessageDefault'));
    setNotify(true);
    reset();
  };

  const canSubmit = (found || missing) && !looking;
  const submitLabel = missing ? t('perm.sendInvite') : t('perm.grant');

  // Giữ tham chiếu mới nhất để hộp thoại cha gọi mà không cần render lại.
  const submitRef = useRef(submit);
  const resetRef = useRef(resetAll);
  submitRef.current = submit;
  resetRef.current = resetAll;

  useEffect(() => {
    onStateChange({
      canSubmit,
      submitLabel,
      submit: () => submitRef.current(),
      reset: () => resetRef.current(),
    });
  }, [canSubmit, submitLabel, onStateChange]);

  async function lookup(): Promise<void> {
    const value = email.trim().toLowerCase();
    reset();
    if (!EMAIL_RE.test(value)) {
      setHint(t('perm.lookupInvalid'));
      return;
    }
    setLooking(true);
    try {
      const data = await permApi.lookup(value, domainId);
      setResult(data);
      if (data.found && data.alreadyGranted) {
        // FR-083 — báo ngay, chặn submit, gợi ý đổi mức quyền.
        setHint(t('perm.lookupGranted', { name: data.user?.fullName ?? value }));
      } else if (data.found) {
        setHint(t('perm.lookupFound'));
      } else {
        setHint(t('perm.lookupMissing'));
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setHint(t('perm.lookupTooMany'));
      else setHint(err instanceof ApiError ? err.message : t('state.error.title'));
    } finally {
      setLooking(false);
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor={`${idPrefix}-email`}>{t('perm.email')}</label>
        <div className="email-lookup">
          <input
            type="email"
            id={`${idPrefix}-email`}
            placeholder={t('perm.emailPlaceholder')}
            autoComplete="off"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              reset();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void lookup();
              }
            }}
          />
          <button
            className="btn btn-primary"
            type="button"
            disabled={looking}
            onClick={() => void lookup()}
          >
            {looking ? t('common.loading') : t('perm.lookup')}
          </button>
        </div>
        <span className="hint">{hint}</span>
      </div>

      {found && result?.user ? (
        <div className="stack">
          <div className="lookup-card">
            <span className={`avatar sm ${avatarTone(result.user.email)}`}>
              {result.user.initials}
            </span>
            <span className="who">
              <b>{result.user.fullName}</b>
              <span>{result.user.email}</span>
            </span>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => {
                setEmail('');
                reset();
              }}
            >
              {t('perm.changeEmail')}
            </button>
          </div>
          <div className="field">
            <label htmlFor={`${idPrefix}-level`}>{t('perm.col.level')}</label>
            <select
              id={`${idPrefix}-level`}
              value={grantRoleId}
              onChange={(event) => setGrantRoleId(event.target.value)}
            >
              {assignable.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <span className="hint">
              {assignable.find((role) => role.id === grantRoleId)?.description ?? ''}
            </span>
          </div>
          <div className="setting-row" style={{ paddingTop: 4, borderBottom: 0 }}>
            <div className="grow">
              <b>{t('perm.notify')}</b>
              <div className="sub">{t('perm.notifySub')}</div>
            </div>
            <label className="switch" aria-label={t('perm.notify')}>
              <input
                type="checkbox"
                checked={notify}
                onChange={(event) => setNotify(event.target.checked)}
              />
              <span className="track" />
            </label>
          </div>
        </div>
      ) : null}

      {missing ? (
        <div className="stack">
          <div className="scope-note warn">
            <Icon name="warn" size={17} />
            <span>{t('perm.inviteWarn', { email: email.trim().toLowerCase() })}</span>
          </div>
          <div className="field">
            <label htmlFor={`${idPrefix}-invite-name`}>{t('perm.inviteName')}</label>
            <input
              type="text"
              id={`${idPrefix}-invite-name`}
              placeholder={t('perm.inviteNamePlaceholder')}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={`${idPrefix}-invite-level`}>{t('perm.inviteLevel')}</label>
            <select
              id={`${idPrefix}-invite-level`}
              value={inviteRoleId}
              onChange={(event) => setInviteRoleId(event.target.value)}
            >
              {assignable.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${idPrefix}-invite-msg`}>{t('perm.inviteMessage')}</label>
            <textarea
              id={`${idPrefix}-invite-msg`}
              rows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>
          <div className="setting-row" style={{ paddingTop: 4, borderBottom: 0 }}>
            <div className="grow">
              <b>{t('perm.inviteExpiry')}</b>
              <div className="sub">{t('perm.inviteExpirySub', { days: expiresInDays })}</div>
            </div>
            <select
              style={{ width: 150 }}
              aria-label={t('perm.inviteExpiry')}
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(Number(event.target.value))}
            >
              {EXPIRY_CHOICES.map((days) => (
                <option key={days} value={days}>
                  {t('perm.inviteDays', { days })}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}
    </>
  );
}
